import { useCallback, useRef, useState, useEffect } from 'react';

export default function useRowChangeTracker({
  getRowId,
  makeSnapshot,
  onBlockSwitch,
  logPrefix = '[RowTracker]',
}) {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [editableRowId, setEditableRowId] = useState(null);

  const snapshotsRef = useRef({});
  const warnLockRef = useRef(false);
  const timeoutRef = useRef(null);
  const isValidRef = useRef(true);

  // Add cleanup effect
  useEffect(() => {
    isValidRef.current = true;
    return () => {
      isValidRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      snapshotsRef.current = {};
    };
  }, []);

  // Helper function to safely get row ID
  const safeGetRowId = useCallback((row) => {
    if (!row || !getRowId) return null;
    const id = getRowId(row);
    return id !== null && id !== undefined ? String(id) : null;
  }, [getRowId]);

  // Helper function for deep equality comparison
  const deepEqual = useCallback((obj1, obj2) => {
    if (obj1 === obj2) return true;
    if (!obj1 || !obj2) return false;
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;

    const keys1 = Object.keys(obj1).sort();
    const keys2 = Object.keys(obj2).sort();

    if (keys1.length !== keys2.length) return false;
    if (keys1.join(',') !== keys2.join(',')) return false;

    return keys1.every(key => deepEqual(obj1[key], obj2[key]));
  }, []);

  const labelOf = useCallback((row) => {
    return row?.__rowLabel || row?.RawMaterialName || row?.RawMaterialCode || `RM-${safeGetRowId(row) ?? 'unknown'}`;
  }, [safeGetRowId]);

  const ensureSnapshot = useCallback((row) => {
    if (!isValidRef.current || !row) return;

    const id = safeGetRowId(row);
    if (!id) return;

    if (!snapshotsRef.current[id]) {
      try {
        if (makeSnapshot) {
          snapshotsRef.current[id] = makeSnapshot(row);
        } else {
          // Fallback snapshot creation
          snapshotsRef.current[id] = { ...row };
        }
      } catch (error) {
        console.error(`${logPrefix} Failed to create snapshot for row ${id}:`, error);
        snapshotsRef.current[id] = { ...row };
      }
    }
  }, [safeGetRowId, makeSnapshot, logPrefix]);

  const hasChanged = useCallback((row) => {
    if (!isValidRef.current || !row) return false;

    const id = safeGetRowId(row);
    if (!id) return false;

    const snap = snapshotsRef.current[id];
    if (!snap) return false;

    try {
      let currentData;
      if (makeSnapshot) {
        currentData = makeSnapshot(row);
      } else {
        currentData = { ...row };
      }

      return !deepEqual(currentData, snap);
    } catch (error) {
      console.error(`${logPrefix} Failed to compare row ${id}:`, error);
      return false;
    }
  }, [safeGetRowId, makeSnapshot, deepEqual, logPrefix]);

  const clearSnapshot = useCallback((rowId) => {
    if (!isValidRef.current) return;

    const id = String(rowId);
    if (id && snapshotsRef.current[id]) {
      delete snapshotsRef.current[id];
    }
  }, []);

  const beginEdit = useCallback((params) => {
    if (!isValidRef.current) return false;

    const targetIndex = params?.node?.rowIndex;
    const targetRow = params?.data;
    const targetRowId = safeGetRowId(targetRow);

    // already editing this row → allow (compare by ID, not index)
    if (editableRowId && targetRowId && editableRowId === targetRowId) return true;

    // block switching if current row has unsaved changes (compare by ID)
    if (hasUnsavedChanges && editableRowId && targetRowId && editableRowId !== targetRowId) {
      if (!warnLockRef.current) {
        warnLockRef.current = true;
        onBlockSwitch?.({ fromIndex: editingRowIndex, toIndex: targetIndex, editingRowId: editableRowId });

        // Clear existing timeout before setting new one
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          if (isValidRef.current) {
            warnLockRef.current = false;
            timeoutRef.current = null;
          }
        }, 1000);
      }
      return false;
    }

    // capture baseline and mark row as active
    setEditingRowIndex(targetIndex);
    setEditableRowId(targetRowId);

    ensureSnapshot(targetRow);
    return true;
  }, [editingRowIndex, hasUnsavedChanges, safeGetRowId, ensureSnapshot, onBlockSwitch, editableRowId]);
  const onValueChange = useCallback((params) => {
    if (!isValidRef.current) return;

    const row = params?.data;
    if (!row) return;

    ensureSnapshot(row);

    if (hasChanged(row)) {
      const currentRowId = safeGetRowId(row);
      setHasUnsavedChanges(true);
      setEditingRowIndex(params?.node?.rowIndex ?? null);
      setEditableRowId(currentRowId);
    } else {
      // if user reverted back to original values, clear highlight
      const currentRowId = safeGetRowId(row);
      if (currentRowId && currentRowId === editableRowId) {
        setHasUnsavedChanges(false);
        setEditingRowIndex(null);
        setEditableRowId(null);
        clearSnapshot(currentRowId);
      }
    }
  }, [ensureSnapshot, hasChanged, safeGetRowId, editableRowId, clearSnapshot]);

  // Convenience: mark current row as editing without forcing change (useful when opening drawers)
  const markEditing = useCallback((params) => {
    if (!isValidRef.current) return;

    const targetIndex = params?.node?.rowIndex ?? null;
    const targetRow = params?.data;
    const targetRowId = safeGetRowId(targetRow);
    setEditingRowIndex(targetIndex);
    setEditableRowId(targetRowId);
  }, [safeGetRowId]);

  const rowStyle = useCallback((params) => {
    if (!isValidRef.current) return null;

    const rowData = params?.data;
    const rowId = safeGetRowId(rowData);
    const shouldHighlight = hasUnsavedChanges && rowId && rowId === editableRowId;
    const isSourceVendor = rowData?.IsSourceVendor === true;

    // Priority: unsaved changes (red) > source vendor (light blue)
    if (shouldHighlight) {
      return { backgroundColor: '#ffe6e6' }; // Red for unsaved changes
    } else if (isSourceVendor) {
      return { backgroundColor: '#e6f3ff' }; // Light blue for source vendor
    }
    return null;
  }, [hasUnsavedChanges, editableRowId, safeGetRowId]);

  const saveSucceeded = useCallback((rowId) => {
    if (!isValidRef.current) return;

    setHasUnsavedChanges(false);
    setEditingRowIndex(null);
    setEditableRowId(null);
    clearSnapshot(rowId);

    // Clear any pending warning timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      warnLockRef.current = false;
    }
  }, [clearSnapshot]);

  const resetAll = useCallback(() => {
    if (!isValidRef.current) return;

    setHasUnsavedChanges(false);
    setEditingRowIndex(null);
    setEditableRowId(null);
    snapshotsRef.current = {};

    // Clear any pending warning timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    warnLockRef.current = false;
  }, []);

  return {
    state: { hasUnsavedChanges, editingRowIndex, editableRowId },
    beginEdit,
    onValueChange,
    rowStyle,
    saveSucceeded,
    resetAll,
    markEditing,
  };
}